import type {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';

import { DonorFertilizationLink } from '@/components/common/links/DonorFertilizationLink';
import { EXPORT_VALUES } from '@/constants';

import { BILL_ACCOUNT_CANING_OPTION } from '../constants';

// Brand colors (from global CSS variables, inlined for Tailwind-first approach)
const TOG = {
  green: '#2ebc25',
  lightGreen: '#90eb89',
  red: '#d71925',
  lightRed: '#e08c8c',
  orange: '#ffa500',
  yellow: '#ffff00',
  blue: '#0000ff',
  darkBlue: '#000080',
  maroon: '#800000',
  lightBlueShaded: '#9aceff',
} as const;

/**
 * Export cell coloring — matches v1 exactly.
 */
function getExportCellStyle(exportValue: string | null) {
  if (
    exportValue === EXPORT_VALUES.NON_EU ||
    exportValue === EXPORT_VALUES.WASH
  ) {
    return { backgroundColor: TOG.red };
  }
  if (exportValue?.includes(EXPORT_VALUES.EU)) {
    return { backgroundColor: TOG.orange };
  }
  if (exportValue?.includes(EXPORT_VALUES.AU)) {
    return { backgroundColor: TOG.yellow };
  }
  if (exportValue?.includes(EXPORT_VALUES.SA)) {
    return { backgroundColor: TOG.maroon, color: 'white' };
  }
  if (
    exportValue?.includes(EXPORT_VALUES.UK) ||
    exportValue === null ||
    exportValue === ''
  ) {
    return { backgroundColor: TOG.lightBlueShaded };
  }
  return {};
}

/**
 * Freeze method coloring — background + white text, matching v1.
 */
function getFreezeMethodStyle(value: string | null) {
  if (!value) return {};
  if (value.includes('Glycerol')) {
    return { backgroundColor: TOG.blue, color: 'white' };
  }
  if (value.includes('Vitrification')) {
    return { backgroundColor: TOG.darkBlue, color: 'white' };
  }
  if (value !== '1.5M EG with suc') {
    return { backgroundColor: TOG.red, color: 'white' };
  }
  return {};
}

/**
 * Cane color coding — only applies when feature flag is enabled.
 */
function getCaneStyle(billAccountCaningOption: number | null | undefined) {
  if (billAccountCaningOption === BILL_ACCOUNT_CANING_OPTION.GROUPS_OF_THREE) {
    return {
      backgroundColor: TOG.blue,
      fontWeight: 'bold' as const,
      color: 'white',
    };
  }
  if (billAccountCaningOption === BILL_ACCOUNT_CANING_OPTION.ODDS_AND_EVENS) {
    return { backgroundColor: TOG.orange, fontWeight: 'bold' as const };
  }
  return { fontWeight: 'bold' as const };
}

/**
 * Validation cell coloring for the checkbox/selection column.
 * Matches v1's full site-aware logic:
 *  - Green = all accounted for / noEmbryosProduced / discards match held
 *  - Yellow = has interim embryos still pending
 *  - Orange = no embryos exist yet (intEmbs=0 & embs=0) and discards don't match
 *  - Red = total mismatch / shortage
 */
function getValidationCellStyle(params: ICellRendererParams) {
  const data = params.data;
  if (!data) return {};

  const embs = data.embs ?? 0;
  const intEmbs = data.intEmbs ?? 0;
  const degenerates = data.degenerates ?? 0;
  const discarded = data.discarded ?? 0;
  const discardedG1 = data.discardedGrade1 ?? 0;
  const discardedG2 = data.discardedGrade2 ?? 0;
  const held = data.heldAtTogToFreeze ?? 0;
  const totalDiscards = degenerates + discarded + discardedG2 + discardedG1;

  if (data.noEmbryosProduced) {
    return { backgroundColor: TOG.green };
  }

  if (intEmbs === 0 && embs === 0) {
    if (held === totalDiscards && held > 0) {
      return { backgroundColor: TOG.green };
    }
    return { backgroundColor: TOG.orange };
  }

  const total = embs + intEmbs + totalDiscards;

  // Site-aware logic: sites 1/2/3 use >= comparison, others use strict ===
  const siteId = params.context?.rawFertilizationSiteId;
  const isTogSite = siteId === '1' || siteId === '2' || siteId === '3';

  if (isTogSite) {
    if (total < held) return { backgroundColor: TOG.red };
    if (intEmbs > 0) return { backgroundColor: TOG.yellow };
    if (intEmbs === 0 && embs === held) return { backgroundColor: TOG.green };
    if (held === totalDiscards && held > 0)
      return { backgroundColor: TOG.green };
    if (total >= held) return { backgroundColor: TOG.green };
  } else {
    if (total !== held) return { backgroundColor: TOG.red };
    if (intEmbs > 0) return { backgroundColor: TOG.yellow };
    if (intEmbs === 0 && embs + totalDiscards === held) {
      return { backgroundColor: TOG.green };
    }
  }

  return {};
}

/**
 * AG Grid only re-runs cellStyle when a cell's value changes, so the status
 * cell's value is built from every input to getValidationCellStyle. A constant
 * value left the color stale (yellow) until the page was refreshed.
 */
export function validationStatusKey(data: Record<string, unknown> | undefined) {
  if (!data) return '';
  return [
    data.embs,
    data.intEmbs,
    data.degenerates,
    data.discarded,
    data.discardedGrade1,
    data.discardedGrade2,
    data.heldAtTogToFreeze,
    data.noEmbryosProduced,
  ].join('|');
}

export { TOG };

export const fertilizationColDefs: ColDef[] = [
  {
    headerName: '',
    width: 40,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    suppressMovable: true,
    lockPosition: true,
    cellStyle: getValidationCellStyle,
    suppressHeaderMenuButton: true,
    valueGetter: (params) => validationStatusKey(params.data),
    valueFormatter: () => '',
  },
  {
    headerName: '',
    cellRenderer: 'agGroupCellRenderer',
    width: 30,
    suppressMovable: true,
    lockPosition: true,
    cellStyle: (params) => {
      const liveEmbryos = params.data?.embs || 0;
      const totalDiscards =
        (params.data?.discarded || 0) +
        (params.data?.discardedGrade2 || 0) +
        (params.data?.discardedGrade1 || 0) +
        (params.data?.degenerates || 0);
      if (liveEmbryos + totalDiscards < (params.data?.heldAtTogToFreeze || 0)) {
        return { backgroundColor: TOG.lightRed };
      }
      if (
        params?.data?.intEmbs === 0 &&
        params?.data?.embs === 0 &&
        !(
          params?.data?.heldAtTogToFreeze === totalDiscards &&
          (params.data?.heldAtTogToFreeze || 0) > 0
        )
      ) {
        return { backgroundColor: TOG.orange };
      }
      return {};
    },
  },
  {
    field: 'plateNumber',
    headerName: 'Plate',
    width: 70,
    editable: true,
    valueGetter: (params: ValueGetterParams) => {
      const val = params.data?.plateNumber;
      return val === 99 ? '' : val;
    },
    cellStyle: (params) => {
      if (params.data?.noEmbryosProduced) {
        return { backgroundColor: 'lightgrey', cursor: 'not-allowed' };
      }
      if (params.data?.isPlateDuplicate) {
        return { backgroundColor: TOG.red, color: 'black', cursor: 'default' };
      }
      return {};
    },
  },
  {
    field: 'csr',
    headerName: 'CSR',
    width: 80,
    wrapText: true,
    autoHeight: true,
  },
  { field: 'account', headerName: 'Account', width: 90 },
  { field: 'opuSite', headerName: 'OPU Site', width: 70 },
  {
    field: 'donorName',
    headerName: 'Donor',
    width: 120,
    wrapText: true,
    autoHeight: true,
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: ICellRendererParams) => ({
      fertilizationId: params.data?.fertId as string,
      donorName: params.data?.donorName as string,
    }),
    cellStyle: (params) => {
      if (params.data?.isDonorDuplicate) {
        return { backgroundColor: TOG.green, fontWeight: 'bold' };
      }
      return { fontWeight: 'bold' };
    },
  },
  {
    field: 'dBreed',
    headerName: 'D Breed',
    width: 70,
    cellStyle: (params) => {
      if (params.data?.breedCategory === 100000001) {
        return { backgroundColor: TOG.lightGreen, fontWeight: 'bold' };
      }
      return {};
    },
  },
  {
    field: 'sireName',
    headerName: 'Sire',
    width: 100,
    wrapText: true,
    autoHeight: true,
  },
  { field: 'sBreed', headerName: 'S Breed', width: 70 },
  { field: 'semenType', headerName: 'Semen Type', width: 80 },
  { field: 'ivcNumber', headerName: 'IVC#', width: 60, editable: true },
  {
    field: 'cleaved',
    headerName: 'Cleaved',
    width: 70,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'predictedGood',
    headerName: 'Quality',
    width: 70,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'predictedVeryLow',
    headerName: 'Low',
    width: 60,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'heldAtTogToFreeze',
    headerName: 'Held Freeze',
    width: 90,
    editable: true,
    type: 'numericColumn',
  },
  // Hidden by default, matching Freeze v1. Main Farm grading enables these
  // through the column menu; WithLocalStorage persists that choice.
  {
    field: 'heldTransferLg',
    headerName: 'Frz Trf LG',
    headerTooltip: 'Frz Trf LG',
    width: 90,
    type: 'numericColumn',
    hide: true,
  },
  {
    field: 'heldTransfer',
    headerName: 'Trf',
    headerTooltip: 'Trf',
    width: 70,
    type: 'numericColumn',
    hide: true,
  },
  {
    field: 'discardedGrade1',
    headerName: 'DCG1',
    width: 60,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'discardedGrade2',
    headerName: 'DCG2',
    width: 60,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'discarded',
    headerName: 'DCG3',
    width: 60,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'degenerates',
    headerName: 'Degen',
    width: 65,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'noEmbryosProduced',
    headerName: 'NEP',
    width: 55,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    editable: true,
    valueGetter: (params: ValueGetterParams) =>
      !!params.data?.noEmbryosProduced,
  },
  {
    field: 'contaminantPresent',
    headerName: 'CP',
    width: 50,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    editable: true,
    valueGetter: (params: ValueGetterParams) =>
      !!params.data?.contaminantPresent,
  },
  {
    field: 'cane',
    headerName: 'Cane',
    width: 80,
    cellStyle: (params) => getCaneStyle(params.data?.billAccountCaningOption),
  },
  {
    field: 'frozen',
    headerName: 'Frozen',
    width: 70,
    editable: true,
    type: 'numericColumn',
  },
  {
    field: 'export',
    headerName: 'Export',
    width: 70,
    cellStyle: (params) => getExportCellStyle(params.value),
  },
  {
    field: 'approvalNumber',
    headerName: 'Approval#',
    width: 110,
    valueGetter: (params: ValueGetterParams) =>
      params.data?.approvalNumber ?? '',
    cellStyle: (params) => (params.value ? { fontWeight: 600 } : {}),
  },
  {
    field: 'freezeMethod',
    headerName: 'Freeze Method',
    width: 120,
    cellStyle: (params) => getFreezeMethodStyle(params.value),
  },
  { field: 'programName', headerName: 'Program', width: 120 },
  {
    field: 'predictionTech',
    headerName: 'Pred Tech',
    width: 90,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: 'csrComment',
    headerName: 'CSR Comment',
    width: 170,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: 'resultsComment',
    headerName: 'Results Comment',
    width: 170,
    editable: true,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: 'intEmbs',
    headerName: 'Int Embs',
    width: 80,
    type: 'numericColumn',
  },
  { field: 'embs', headerName: 'Embs', width: 65, type: 'numericColumn' },
];
